import { useMemo, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ApiOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { useIsMobile } from '@/lib/hooks'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'

export const Route = createFileRoute('/_authed/tmdb')({
  component: TmdbPage,
})

const PAGE_SIZE = 50
const NUM_STYLE = { fontVariantNumeric: 'tabular-nums' } as const

type BackfillMethod = 'wikidata' | 'title_match'
type MappingStatus = 'verified' | 'pending' | 'rejected'
type MediaType = 'tv' | 'movie'
type MatchMethod =
  | 'wikidata'
  | 'title_match'
  | 'series_inherit'
  | 'manual'
  | 'tmdb_search'
  | 'bangumi_hint'

interface TmdbMappingRow {
  id: number
  anime_id: number
  title: string
  title_original: string | null
  aliases: string[] | null
  release_year: number | null
  format: string | null
  bangumi_id: number | null
  series_id: number | null
  anime_season_number: number | null
  tmdb_id: number
  media_type: MediaType
  tmdb_season_number: number | null
  status: MappingStatus
  confidence: number | null
  match_method: MatchMethod
  evidence: Record<string, unknown> | null
  has_profile: boolean
  asset_count: number
  synced_at: string | null
  created_at: string | null
  updated_at: string | null
}

interface MappingListResult {
  ok: boolean
  page?: number
  page_size?: number
  total?: number
  rows?: TmdbMappingRow[]
  error?: string
}

interface MappingStatusResult {
  ok: boolean
  mapping_id?: number
  anime_id?: number
  old_status?: MappingStatus
  new_status?: MappingStatus
  conflict_mapping_id?: number
  error?: string
}

interface MappingDeleteResult {
  ok: boolean
  mapping_id?: number
  anime_id?: number
  deleted?: boolean
  profile_asset_cascade?: boolean
  profile_count?: number
  asset_count?: number
  error?: string
}

interface TmdbBackfillCandidate {
  id: number
  title: string
  title_original: string | null
  aliases: string[] | null
  release_year: number | null
  format: string | null
  bangumi_id: number | null
  series_id: number | null
  season_number: number | null
}

interface BackfillCandidateResult {
  ok: boolean
  method?: BackfillMethod
  page?: number
  page_size?: number
  total?: number
  rows?: TmdbBackfillCandidate[]
  error?: string
}

interface SeriesInheritCandidate {
  anime_id: number
  title: string
  title_original: string | null
  season_number: number | null
  season_subtitle: string | null
  release_year: number | null
  series_id: number | null
  tmdb_id: number
  media_type: string
  source_anime_id: number
  source_title: string | null
  source_title_original: string | null
  source_season_number: number | null
  source_mapping_id: number
  source_confidence: number | null
  source_match_method: string | null
}

interface SeriesInheritResult {
  ok: boolean
  page?: number
  page_size?: number
  include_movies?: boolean
  rows?: SeriesInheritCandidate[]
  error?: string
}

interface ApplySeriesInheritResult {
  ok: boolean
  dry_run?: boolean
  applied_count?: number
  error?: string
}

interface ManualMappingResult {
  ok: boolean
  mapping_id?: number
  anime_id?: number
  title?: string | null
  tmdb_id?: number
  media_type?: MediaType
  tmdb_season_number?: number | null
  status?: MappingStatus
  match_method?: MatchMethod
  sync_required?: boolean
  conflict_mapping_id?: number
  error?: string
}

interface SyncMappingResourcesResult {
  ok: boolean
  mapping_id?: number
  anime_id?: number
  tmdb_id?: number
  media_type?: MediaType
  sync_kind?: 'initial_manual_sync' | 'resource_refresh'
  asset_count?: number
  profile_updated?: boolean
  error?: string
  message?: string
}

type FunctionErrorBody = {
  error?: unknown
  message?: unknown
}

type FunctionHttpError = Error & {
  context?: Response
}

interface ManualAssignFormValues {
  media_type: MediaType
  tmdb_id: number
  season_number?: number | null
}

const STATUS_OPTIONS: Array<{ label: string; value: MappingStatus }> = [
  { label: '已确认', value: 'verified' },
  { label: '待确认', value: 'pending' },
  { label: '已驳回', value: 'rejected' },
]

const MEDIA_TYPE_OPTIONS: Array<{ label: string; value: MediaType }> = [
  { label: '剧集', value: 'tv' },
  { label: '电影', value: 'movie' },
]

const MATCH_METHOD_OPTIONS: Array<{ label: string; value: MatchMethod }> = [
  { label: 'Wikidata', value: 'wikidata' },
  { label: '标题匹配', value: 'title_match' },
  { label: '系列继承', value: 'series_inherit' },
  { label: '手动录入', value: 'manual' },
  { label: 'TMDB 搜索', value: 'tmdb_search' },
  { label: 'Bangumi 线索', value: 'bangumi_hint' },
]

const METHOD_OPTIONS: Array<{ label: string; value: BackfillMethod }> = [
  { label: 'Wikidata', value: 'wikidata' },
  { label: '标题匹配', value: 'title_match' },
]

const ERROR_LABELS: Record<string, string> = {
  forbidden: '没有管理员权限',
  invalid_method: '候选来源不合法',
  invalid_status: '映射状态不合法',
  invalid_media_type: 'TMDB 类型不合法',
  invalid_match_method: '匹配来源不合法',
  invalid_mapping_id: '映射 ID 不合法',
  invalid_anime_id: '番剧 ID 不合法',
  invalid_tmdb_id: 'TMDB ID 不合法',
  invalid_season_number: 'TMDB 季数不合法',
  mapping_not_found: '映射不存在',
  mapping_not_refreshable: '该映射当前不可刷新素材',
  anime_not_found: '番剧不存在',
  anime_not_eligible: '该番剧当前不在首批手动同步范围内',
  verified_mapping_conflict: '该番剧已经存在另一个已确认映射',
  tmdb_not_found: 'TMDB 没有找到该资源',
  tmdb_sync_failed: 'TMDB 资源同步失败',
  server_misconfigured: '后端 TMDB 同步服务未配置',
}

const STATUS_COLORS: Record<MappingStatus, string> = {
  verified: 'green',
  pending: 'gold',
  rejected: 'red',
}

const MATCH_METHOD_COLORS: Record<MatchMethod, string> = {
  wikidata: 'blue',
  title_match: 'cyan',
  series_inherit: 'purple',
  manual: 'geekblue',
  tmdb_search: 'volcano',
  bangumi_hint: 'magenta',
}

const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<MappingStatus, string>

const MEDIA_TYPE_LABELS = Object.fromEntries(
  MEDIA_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<MediaType, string>

const MATCH_METHOD_LABELS = Object.fromEntries(
  MATCH_METHOD_OPTIONS.map((option) => [option.value, option.label]),
) as Record<MatchMethod, string>

function mediaTypeLabel(value: string): string {
  return value === 'tv' || value === 'movie' ? MEDIA_TYPE_LABELS[value] : value
}

function matchMethodLabel(value: string): string {
  return value in MATCH_METHOD_LABELS
    ? MATCH_METHOD_LABELS[value as MatchMethod]
    : value
}

function rpcErrorText(error: Error): string {
  return ERROR_LABELS[error.message] ?? error.message
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function functionErrorBodyText(body: FunctionErrorBody | null): string | null {
  if (!body || typeof body !== 'object') return null

  const error = stringValue(body.error)
  const message = stringValue(body.message)
  if (error && message && error !== message) {
    return `${ERROR_LABELS[error] ?? error}：${ERROR_LABELS[message] ?? message}`
  }
  if (error) return ERROR_LABELS[error] ?? error
  if (message) return ERROR_LABELS[message] ?? message
  return null
}

async function edgeFunctionErrorText(error: Error): Promise<string> {
  const context = (error as FunctionHttpError).context
  if (!context) return rpcErrorText(error)

  try {
    const body = (await context.clone().json()) as FunctionErrorBody
    const text = functionErrorBodyText(body)
    if (text) return text
  } catch {
    // Fall back to the raw response text below.
  }

  try {
    const text = await context.clone().text()
    if (text.trim()) return text.trim()
  } catch {
    // Fall back to the original Supabase error message.
  }

  return rpcErrorText(error)
}

function isRpcMissingError(error: Error): boolean {
  const text =
    `${error.message} ${(error as { details?: string }).details ?? ''} ${(error as { hint?: string }).hint ?? ''}`.toLowerCase()
  return (
    text.includes('could not find the function') ||
    text.includes('schema cache') ||
    text.includes('pgrst202')
  )
}

function errorDescription(error: Error) {
  if (!isRpcMissingError(error)) return rpcErrorText(error)

  return (
    <Space direction="vertical" size={4}>
      <Typography.Text>
        后台 TMDB 管理 RPC 还没有部署到当前 Supabase 项目，或 PostgREST schema
        cache 尚未刷新。
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        应用迁移：20260623232628_admin_tmdb_mapping_manager.sql
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        部署命令：cd backend &amp;&amp; supabase db push
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        前端变更后如路由未出现，请重启 antd-demo dev server。
      </Typography.Text>
    </Space>
  )
}

function scriptCommand(method: BackfillMethod): string {
  if (method === 'wikidata') {
    return 'node backend/supabase/scripts/tmdb_backfill.mjs --method wikidata --limit 50 --dry-run'
  }
  return 'node backend/supabase/scripts/tmdb_backfill.mjs --method title_match --limit 50 --dry-run --interactive'
}

function percentText(value: number | null): string {
  if (value == null) return '-'
  return `${Math.round(value * 100)}%`
}

function textOrDash(
  value: string | number | null | undefined,
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <Typography.Text type="secondary">-</Typography.Text>
  }
  return value
}

function tmdbResourceUrl(row: Pick<TmdbMappingRow, 'media_type' | 'tmdb_id'>) {
  const type = row.media_type === 'tv' ? 'tv' : 'movie'
  return `https://www.themoviedb.org/${type}/${row.tmdb_id}`
}

function defaultMediaType(
  row: Pick<TmdbBackfillCandidate, 'format'>,
): MediaType {
  return String(row.format ?? '').toLowerCase() === 'movie' ? 'movie' : 'tv'
}

function TmdbPage() {
  const { message, modal } = AntdApp.useApp()
  const [manualForm] = Form.useForm<ManualAssignFormValues>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [activeTab, setActiveTab] = useState('mappings')
  const [mappingPage, setMappingPage] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [statuses, setStatuses] = useState<MappingStatus[]>([
    'verified',
    'pending',
    'rejected',
  ])
  const [mediaTypes, setMediaTypes] = useState<MediaType[]>(['tv', 'movie'])
  const [matchMethods, setMatchMethods] = useState<MatchMethod[]>(
    MATCH_METHOD_OPTIONS.map((o) => o.value),
  )
  const [method, setMethod] = useState<BackfillMethod>('wikidata')
  const [backfillPage, setBackfillPage] = useState(1)
  const [seriesPage, setSeriesPage] = useState(1)
  const [includeMovies, setIncludeMovies] = useState(false)
  const [manualAssignRow, setManualAssignRow] =
    useState<TmdbBackfillCandidate | null>(null)

  const mappingQuery = useQuery({
    queryKey: [
      'admin_tmdb_mappings',
      searchText,
      statuses,
      mediaTypes,
      matchMethods,
      mappingPage,
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'list_tmdb_mappings' as never,
        {
          p_search: searchText.trim() || null,
          p_statuses: statuses.length ? statuses : null,
          p_media_types: mediaTypes.length ? mediaTypes : null,
          p_match_methods: matchMethods.length ? matchMethods : null,
          p_page: mappingPage,
          p_page_size: PAGE_SIZE,
        } as never,
      )
      if (error) throw error
      const result = data as MappingListResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
  })

  const backfillQuery = useQuery({
    queryKey: ['admin_tmdb_backfill_candidates', method, backfillPage],
    enabled: activeTab === 'backfill',
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'list_tmdb_backfill_candidates' as never,
        {
          p_method: method,
          p_page: backfillPage,
          p_page_size: PAGE_SIZE,
        } as never,
      )
      if (error) throw error
      const result = data as BackfillCandidateResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
  })

  const seriesQuery = useQuery({
    queryKey: [
      'admin_tmdb_series_inherit_candidates',
      seriesPage,
      includeMovies,
    ],
    enabled: activeTab === 'series',
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'list_tmdb_series_inherit_candidates' as never,
        {
          p_page: seriesPage,
          p_page_size: PAGE_SIZE,
          p_include_movies: includeMovies,
        } as never,
      )
      if (error) throw error
      const result = data as SeriesInheritResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
  })

  const statusMutation = useMutation({
    mutationFn: async (payload: {
      mappingId: number
      status: MappingStatus
      reason: string
    }) => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'set_tmdb_mapping_status' as never,
        {
          p_mapping_id: payload.mappingId,
          p_new_status: payload.status,
          p_reason: payload.reason,
        } as never,
      )
      if (error) throw error
      const result = data as MappingStatusResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
    onSuccess: (result) => {
      message.success(
        `映射 #${result.mapping_id ?? '-'} 已设为 ${result.new_status ? STATUS_LABELS[result.new_status] : '-'}`,
      )
      invalidateTmdbQueries()
    },
    onError: (error: Error) => {
      message.error(`状态修改失败：${rpcErrorText(error)}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (payload: { mappingId: number; reason: string }) => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'delete_tmdb_mapping' as never,
        {
          p_mapping_id: payload.mappingId,
          p_reason: payload.reason,
        } as never,
      )
      if (error) throw error
      const result = data as MappingDeleteResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
    onSuccess: (result) => {
      message.success(
        `已删除映射 #${result.mapping_id ?? '-'}，级联清理资料 ${result.profile_count ?? 0} 条、资源 ${result.asset_count ?? 0} 条`,
      )
      invalidateTmdbQueries()
    },
    onError: (error: Error) => {
      message.error(`删除失败：${rpcErrorText(error)}`)
    },
  })

  const applyMutation = useMutation({
    mutationFn: async (payload: {
      limit: number
      includeMovies: boolean
      reason: string
    }) => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'apply_tmdb_series_inherit' as never,
        {
          p_limit: payload.limit,
          p_include_movies: payload.includeMovies,
          p_reason: payload.reason,
        } as never,
      )
      if (error) throw error
      const result = data as ApplySeriesInheritResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
    onSuccess: (result) => {
      message.success(`已应用 ${result.applied_count ?? 0} 条系列继承`)
      invalidateTmdbQueries()
    },
    onError: (error: Error) => {
      message.error(`应用失败：${rpcErrorText(error)}`)
    },
  })

  const syncMappingResourcesMutation = useMutation({
    mutationFn: async (payload: { mappingId: number }) => {
      const { data, error } = await supabase.functions.invoke(
        'sync-tmdb-manual-mapping',
        {
          body: { mapping_id: payload.mappingId },
        },
      )
      if (error) throw new Error(await edgeFunctionErrorText(error))
      const result = data as SyncMappingResourcesResult
      if (!result.ok) {
        throw new Error(functionErrorBodyText(result) ?? 'unknown')
      }
      return result
    },
    onSuccess: (result) => {
      message.success(
        result.sync_kind === 'resource_refresh'
          ? `TMDB 素材已刷新：映射 #${result.mapping_id ?? '-'}，本次返回 ${result.asset_count ?? 0} 个资源`
          : `TMDB 资源已同步：映射 #${result.mapping_id ?? '-'}，资源 ${result.asset_count ?? 0} 个`,
      )
      invalidateTmdbQueries()
    },
    onError: (error: Error) => {
      message.error(`资源同步失败：${rpcErrorText(error)}`)
    },
  })

  const manualAssignMutation = useMutation({
    mutationFn: async (payload: {
      row: TmdbBackfillCandidate
      values: ManualAssignFormValues
    }) => {
      const { data, error } = await supabase.schema('admin' as never).rpc(
        'upsert_tmdb_manual_mapping' as never,
        {
          p_anime_id: payload.row.id,
          p_tmdb_id: payload.values.tmdb_id,
          p_media_type: payload.values.media_type,
          p_season_number: payload.values.season_number ?? null,
          p_reason: `manual tmdb id from missing candidate ${payload.row.id}`,
        } as never,
      )
      if (error) throw error
      const result = data as ManualMappingResult
      if (!result.ok) throw new Error(result.error ?? 'unknown')
      return result
    },
    onSuccess: (result) => {
      message.success(
        `已手动指定 TMDB ID：#${result.mapping_id ?? '-'}，开始同步资源`,
      )
      invalidateTmdbQueries()
    },
    onError: (error: Error) => {
      message.error(`手动指定失败：${rpcErrorText(error)}`)
    },
  })

  function invalidateTmdbQueries() {
    queryClient.invalidateQueries({ queryKey: ['admin_tmdb_mappings'] })
    queryClient.invalidateQueries({
      queryKey: ['admin_tmdb_backfill_candidates'],
    })
    queryClient.invalidateQueries({
      queryKey: ['admin_tmdb_series_inherit_candidates'],
    })
  }

  const command = scriptCommand(method)
  const mappingRows = mappingQuery.data?.rows ?? []
  const backfillRows = backfillQuery.data?.rows ?? []
  const seriesRows = seriesQuery.data?.rows ?? []

  const mappingColumns: ColumnsType<TmdbMappingRow> = useMemo(
    () => [
      {
        title: '映射',
        width: 120,
        fixed: 'left',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong style={NUM_STYLE}>
              #{row.id}
            </Typography.Text>
            <Tag color={STATUS_COLORS[row.status]}>
              {STATUS_LABELS[row.status]}
            </Tag>
          </Space>
        ),
      },
      {
        title: '番剧',
        width: 260,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Link
              to="/anime/$animeId/edit"
              params={{ animeId: String(row.anime_id) }}
            >
              <Typography.Text strong>{row.title}</Typography.Text>
            </Link>
            {row.title_original ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.title_original}
              </Typography.Text>
            ) : null}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{row.anime_id} · {row.release_year ?? '-'} · {row.format ?? '-'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'TMDB',
        width: 140,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Link href={tmdbResourceUrl(row)} target="_blank">
              <Typography.Text style={NUM_STYLE}>{row.tmdb_id}</Typography.Text>
            </Typography.Link>
            <Space size={4}>
              <Tag>{mediaTypeLabel(row.media_type)}</Tag>
              {row.tmdb_season_number == null ? null : (
                <Tag>第 {row.tmdb_season_number} 季</Tag>
              )}
            </Space>
          </Space>
        ),
      },
      {
        title: '来源',
        width: 160,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Tag color={MATCH_METHOD_COLORS[row.match_method]}>
              {matchMethodLabel(row.match_method)}
            </Tag>
            <Typography.Text type="secondary" style={NUM_STYLE}>
              {percentText(row.confidence)}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '资料',
        width: 110,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{row.has_profile ? '资料' : '-'}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              资源 {row.asset_count}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '更新',
        dataIndex: 'updated_at',
        width: 180,
        render: (value: string | null) =>
          value ? (
            <Typography.Text style={NUM_STYLE}>
              {value.replace('T', ' ').slice(0, 19)}
            </Typography.Text>
          ) : (
            textOrDash(value)
          ),
      },
      {
        title: '操作',
        width: 260,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={6} wrap>
            {STATUS_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="small"
                disabled={
                  row.status === option.value || statusMutation.isPending
                }
                onClick={() => confirmStatusChange(row, option.value)}
              >
                {option.label}
              </Button>
            ))}
            {row.match_method === 'manual' && row.status === 'pending' ? (
              <Button
                size="small"
                icon={<ThunderboltOutlined />}
                disabled={syncMappingResourcesMutation.isPending}
                loading={syncMappingResourcesMutation.isPending}
                onClick={() =>
                  syncMappingResourcesMutation.mutate({ mappingId: row.id })
                }
              >
                同步资源
              </Button>
            ) : null}
            {row.status === 'verified' ? (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                disabled={syncMappingResourcesMutation.isPending}
                loading={syncMappingResourcesMutation.isPending}
                onClick={() => confirmResourceRefresh(row)}
              >
                刷新素材
              </Button>
            ) : null}
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={deleteMutation.isPending}
              onClick={() => confirmDeleteMapping(row)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [
      deleteMutation.isPending,
      statusMutation.isPending,
      syncMappingResourcesMutation,
      syncMappingResourcesMutation.isPending,
    ],
  )

  const backfillColumns: ColumnsType<TmdbBackfillCandidate> = useMemo(
    () => [
      {
        title: 'Anime ID',
        dataIndex: 'id',
        width: 100,
        render: (id: number) => (
          <Link to="/anime/$animeId/edit" params={{ animeId: String(id) }}>
            <Typography.Text style={NUM_STYLE}>{id}</Typography.Text>
          </Link>
        ),
      },
      {
        title: '标题',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{row.title}</Typography.Text>
            {row.title_original ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.title_original}
              </Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: '年份',
        dataIndex: 'release_year',
        width: 90,
        render: textOrDash,
      },
      {
        title: '格式',
        dataIndex: 'format',
        width: 100,
        render: (value: string | null) =>
          value ? (
            <Tag>{value}</Tag>
          ) : (
            <Typography.Text type="secondary">-</Typography.Text>
          ),
      },
      {
        title: 'Bangumi',
        dataIndex: 'bangumi_id',
        width: 110,
        render: textOrDash,
      },
      {
        title: '系列',
        dataIndex: 'series_id',
        width: 100,
        render: textOrDash,
      },
      {
        title: '季数',
        dataIndex: 'season_number',
        width: 80,
        render: textOrDash,
      },
      {
        title: '操作',
        width: 230,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={8}>
            <Link
              to="/anime/$animeId/edit"
              params={{ animeId: String(row.id) }}
            >
              编辑
            </Link>
            <Button
              size="small"
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => openManualAssign(row)}
            >
              手动指定 TMDB ID
            </Button>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyCommand(command)}
            >
              命令
            </Button>
          </Space>
        ),
      },
    ],
    [command],
  )

  const seriesColumns: ColumnsType<SeriesInheritCandidate> = useMemo(
    () => [
      {
        title: '目标番剧',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Link
              to="/anime/$animeId/edit"
              params={{ animeId: String(row.anime_id) }}
            >
              <Typography.Text strong>{row.title}</Typography.Text>
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{row.anime_id} · 系列 {row.series_id ?? '-'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '季数/年份',
        width: 120,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>第 {row.season_number ?? '-'} 季</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.release_year ?? '-'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '继承来源',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Link
              to="/anime/$animeId/edit"
              params={{ animeId: String(row.source_anime_id) }}
            >
              {row.source_title ?? `#${row.source_anime_id}`}
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              source #{row.source_anime_id} · 第{' '}
              {row.source_season_number ?? '-'} 季
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'TMDB',
        width: 120,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={NUM_STYLE}>{row.tmdb_id}</Typography.Text>
            <Tag>{mediaTypeLabel(row.media_type)}</Tag>
          </Space>
        ),
      },
      {
        title: '置信度',
        dataIndex: 'source_confidence',
        width: 100,
        render: (value: number | null) => (
          <Typography.Text style={NUM_STYLE}>
            {percentText(value)}
          </Typography.Text>
        ),
      },
      {
        title: '来源方法',
        dataIndex: 'source_match_method',
        width: 130,
        render: (value: string | null) =>
          value ? (
            <Tag color="blue">{matchMethodLabel(value)}</Tag>
          ) : (
            textOrDash(value)
          ),
      },
    ],
    [],
  )

  async function copyCommand(text: string) {
    try {
      await navigator.clipboard.writeText(text)
      message.success('命令已复制')
    } catch {
      message.warning('复制失败，请手动复制命令')
    }
  }

  function confirmStatusChange(row: TmdbMappingRow, status: MappingStatus) {
    modal.confirm({
      title: `把映射 #${row.id} 设为${STATUS_LABELS[status]}？`,
      content:
        status === 'verified'
          ? '同一番剧只能有一个已确认映射；如果已有已确认映射，后端会拒绝本次操作。'
          : '降级已确认映射后，该番剧会重新进入未匹配候选，脚本可重新同步。',
      okText: `设为${STATUS_LABELS[status]}`,
      cancelText: '取消',
      icon: <EditOutlined />,
      okButtonProps: { loading: statusMutation.isPending },
      onOk: () =>
        statusMutation.mutateAsync({
          mappingId: row.id,
          status,
          reason: `admin tmdb mapping status: ${row.status} -> ${status}`,
        }),
    })
  }

  function confirmDeleteMapping(row: TmdbMappingRow) {
    modal.confirm({
      title: `删除 TMDB 映射 #${row.id}？`,
      content:
        '会删除这条映射，并通过外键级联清理关联资料和图片资源。删除后脚本可以重新同步。',
      okText: '删除映射',
      cancelText: '取消',
      okButtonProps: { danger: true, loading: deleteMutation.isPending },
      onOk: () =>
        deleteMutation.mutateAsync({
          mappingId: row.id,
          reason: 'admin tmdb mapping delete for resync',
        }),
    })
  }

  function confirmResourceRefresh(row: TmdbMappingRow) {
    modal.confirm({
      title: `刷新映射 #${row.id} 的 TMDB 素材？`,
      content:
        '会按当前 TMDB ID 重新获取简介、poster、logo 和 backdrop；映射保持已确认，不会重新匹配 TMDB ID。',
      okText: '刷新素材',
      cancelText: '取消',
      icon: <ReloadOutlined />,
      okButtonProps: { loading: syncMappingResourcesMutation.isPending },
      onOk: () =>
        syncMappingResourcesMutation.mutateAsync({ mappingId: row.id }),
    })
  }

  function confirmApplySeriesInherit() {
    const limit = Math.max(1, seriesRows.length || PAGE_SIZE)
    modal.confirm({
      title: '应用 TMDB 系列继承？',
      content: '会为当前候选创建已确认的系列继承映射，不会复制资料和图片资源。',
      okText: '应用继承',
      cancelText: '取消',
      okButtonProps: { loading: applyMutation.isPending },
      onOk: () =>
        applyMutation.mutateAsync({
          limit,
          includeMovies,
          reason: 'admin tmdb manager',
        }),
    })
  }

  function openManualAssign(row: TmdbBackfillCandidate) {
    setManualAssignRow(row)
    manualForm.resetFields()
    manualForm.setFieldsValue({
      media_type: defaultMediaType(row),
      season_number: row.season_number ?? undefined,
    })
  }

  function closeManualAssign() {
    setManualAssignRow(null)
    manualForm.resetFields()
  }

  async function submitManualAssign() {
    if (!manualAssignRow) return
    try {
      const values = await manualForm.validateFields()
      const result = await manualAssignMutation.mutateAsync({
        row: manualAssignRow,
        values,
      })
      closeManualAssign()
      if (result.mapping_id) {
        await syncMappingResourcesMutation.mutateAsync({
          mappingId: result.mapping_id,
        })
      }
    } catch {
      // Mutation onError handlers and Form validation already surface details.
    }
  }

  const mappingError =
    mappingQuery.error instanceof Error ? mappingQuery.error : null
  const backfillError =
    backfillQuery.error instanceof Error ? backfillQuery.error : null
  const seriesError =
    seriesQuery.error instanceof Error ? seriesQuery.error : null

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={6}>
          <Space>
            <ApiOutlined />
            <Typography.Title level={4} style={{ margin: 0 }}>
              TMDB 元数据
            </Typography.Title>
          </Space>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            管理所有 TMDB
            映射状态。错误的已确认映射可降级为待确认/已驳回，或删除映射后让同步脚本重新回填。
          </Typography.Paragraph>
        </Space>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'mappings',
            label: '映射管理',
            children: (
              <Card
                title="所有映射"
                extra={
                  <Tooltip title={isMobile ? '刷新' : undefined}>
                    <Button
                      icon={<ReloadOutlined />}
                      aria-label="刷新"
                      onClick={() => mappingQuery.refetch()}
                      loading={
                        mappingQuery.isFetching && !mappingQuery.isLoading
                      }
                    >
                      {isMobile ? null : '刷新'}
                    </Button>
                  </Tooltip>
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <AdminFilterBar
                    activeCount={
                      (statuses.length < STATUS_OPTIONS.length ? 1 : 0) +
                      (mediaTypes.length < MEDIA_TYPE_OPTIONS.length ? 1 : 0) +
                      (matchMethods.length < MATCH_METHOD_OPTIONS.length
                        ? 1
                        : 0)
                    }
                    primary={
                      <Input.Search
                        allowClear
                        placeholder="搜索标题 / 原名 / Anime ID / TMDB ID"
                        style={{ width: 320 }}
                        onSearch={(value) => {
                          setSearchText(value.trim())
                          setMappingPage(1)
                        }}
                      />
                    }
                  >
                    <Select<MappingStatus[]>
                      mode="multiple"
                      allowClear
                      placeholder="映射状态"
                      className="admin-filter-grid__full"
                      maxTagCount={isMobile ? 'responsive' : undefined}
                      value={statuses}
                      options={STATUS_OPTIONS}
                      style={{ minWidth: 240 }}
                      onChange={(value) => {
                        setStatuses(value)
                        setMappingPage(1)
                      }}
                    />
                    <Select<MediaType[]>
                      mode="multiple"
                      allowClear
                      placeholder="TMDB 类型"
                      className="admin-filter-grid__full"
                      maxTagCount={isMobile ? 'responsive' : undefined}
                      value={mediaTypes}
                      options={MEDIA_TYPE_OPTIONS}
                      style={{ minWidth: 160 }}
                      onChange={(value) => {
                        setMediaTypes(value)
                        setMappingPage(1)
                      }}
                    />
                    <Select<MatchMethod[]>
                      mode="multiple"
                      allowClear
                      placeholder="匹配来源"
                      className="admin-filter-grid__full"
                      maxTagCount={isMobile ? 'responsive' : undefined}
                      value={matchMethods}
                      options={MATCH_METHOD_OPTIONS}
                      style={{ minWidth: 320 }}
                      onChange={(value) => {
                        setMatchMethods(value)
                        setMappingPage(1)
                      }}
                    />
                  </AdminFilterBar>
                  {mappingError ? (
                    <Alert
                      type="error"
                      showIcon
                      message="TMDB 映射加载失败"
                      description={errorDescription(mappingError)}
                    />
                  ) : null}
                  <div className="admin-table-wrap">
                    <Table<TmdbMappingRow>
                      rowKey="id"
                      columns={mappingColumns}
                      dataSource={mappingRows}
                      loading={mappingQuery.isLoading}
                      scroll={{ x: 1230 }}
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="暂无 TMDB 映射"
                          />
                        ),
                      }}
                      pagination={{
                        current: mappingPage,
                        pageSize: PAGE_SIZE,
                        total: mappingQuery.data?.total ?? 0,
                        showSizeChanger: false,
                        onChange: setMappingPage,
                      }}
                    />
                  </div>
                  <AdminMobileList
                    rows={mappingRows}
                    rowKey={(row) => row.id}
                    loading={mappingQuery.isFetching}
                    emptyContent={
                      mappingQuery.isLoading ? undefined : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="暂无 TMDB 映射"
                        />
                      )
                    }
                    page={mappingPage}
                    pageSize={PAGE_SIZE}
                    total={mappingQuery.data?.total ?? 0}
                    onPageChange={setMappingPage}
                    renderItem={(row) => (
                      <article className="admin-mobile-record tmdb-mobile-record">
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title">
                            <Link
                              to="/anime/$animeId/edit"
                              params={{ animeId: String(row.anime_id) }}
                            >
                              {row.title}
                            </Link>
                            {row.title_original ? (
                              <div className="admin-mobile-record__meta">
                                <span>{row.title_original}</span>
                              </div>
                            ) : null}
                          </div>
                          <Tag color={STATUS_COLORS[row.status]}>
                            {STATUS_LABELS[row.status]}
                          </Tag>
                        </div>
                        <div className="admin-mobile-record__badges">
                          <Tag color={MATCH_METHOD_COLORS[row.match_method]}>
                            {matchMethodLabel(row.match_method)}
                            {row.confidence == null
                              ? null
                              : ` ${percentText(row.confidence)}`}
                          </Tag>
                          <Tag>{mediaTypeLabel(row.media_type)}</Tag>
                          {row.tmdb_season_number == null ? null : (
                            <Tag>第 {row.tmdb_season_number} 季</Tag>
                          )}
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span style={NUM_STYLE}>映射 #{row.id}</span>
                          <Typography.Link
                            href={tmdbResourceUrl(row)}
                            target="_blank"
                            style={NUM_STYLE}
                          >
                            TMDB {row.tmdb_id}
                          </Typography.Link>
                          <span>
                            {row.has_profile ? '有资料' : '无资料'} · 资源{' '}
                            {row.asset_count}
                          </span>
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span style={NUM_STYLE}>
                            #{row.anime_id} · {row.release_year ?? '-'} ·{' '}
                            {row.format ?? '-'}
                          </span>
                          {row.updated_at ? (
                            <span style={NUM_STYLE}>
                              更新{' '}
                              {row.updated_at.replace('T', ' ').slice(5, 16)}
                            </span>
                          ) : null}
                        </div>
                        <AdminRecordActions
                          actions={[
                            {
                              key: 'sync',
                              label: '同步资源',
                              icon: <ThunderboltOutlined />,
                              primary: true,
                              visible:
                                row.match_method === 'manual' &&
                                row.status === 'pending',
                              disabled: syncMappingResourcesMutation.isPending,
                              loading: syncMappingResourcesMutation.isPending,
                              onClick: () =>
                                syncMappingResourcesMutation.mutate({
                                  mappingId: row.id,
                                }),
                            },
                            {
                              key: 'refresh',
                              label: '刷新素材',
                              icon: <ReloadOutlined />,
                              primary: true,
                              visible: row.status === 'verified',
                              disabled: syncMappingResourcesMutation.isPending,
                              loading: syncMappingResourcesMutation.isPending,
                              onClick: () => confirmResourceRefresh(row),
                            },
                            ...STATUS_OPTIONS.filter(
                              (option) => option.value !== row.status,
                            ).map((option) => ({
                              key: `status-${option.value}`,
                              label: `设为${option.label}`,
                              primary: option.value === 'verified',
                              disabled: statusMutation.isPending,
                              onClick: () =>
                                confirmStatusChange(row, option.value),
                            })),
                            {
                              key: 'delete',
                              label: '删除',
                              icon: <DeleteOutlined />,
                              danger: true,
                              disabled: deleteMutation.isPending,
                              onClick: () => confirmDeleteMapping(row),
                            },
                          ]}
                        />
                      </article>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'backfill',
            label: '未匹配候选',
            children: (
              <Card
                title="未匹配候选"
                extra={
                  <Space wrap>
                    <Segmented<BackfillMethod>
                      options={METHOD_OPTIONS}
                      value={method}
                      onChange={(value) => {
                        setMethod(value)
                        setBackfillPage(1)
                      }}
                    />
                    <Tooltip title={isMobile ? '刷新' : undefined}>
                      <Button
                        icon={<ReloadOutlined />}
                        aria-label="刷新"
                        onClick={() => backfillQuery.refetch()}
                        loading={
                          backfillQuery.isFetching && !backfillQuery.isLoading
                        }
                      >
                        {isMobile ? null : '刷新'}
                      </Button>
                    </Tooltip>
                  </Space>
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    showIcon
                    message={
                      method === 'wikidata'
                        ? 'Wikidata 回填命令'
                        : '标题匹配回填命令'
                    }
                    description={
                      <Space.Compact style={{ width: '100%' }}>
                        <Typography.Text
                          code
                          copyable
                          style={{
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {command}
                        </Typography.Text>
                      </Space.Compact>
                    }
                  />
                  {backfillError ? (
                    <Alert
                      type="error"
                      showIcon
                      message="未匹配候选加载失败"
                      description={errorDescription(backfillError)}
                    />
                  ) : null}
                  <div className="admin-table-wrap">
                    <Table<TmdbBackfillCandidate>
                      rowKey="id"
                      columns={backfillColumns}
                      dataSource={backfillRows}
                      loading={backfillQuery.isLoading}
                      scroll={{ x: 1000 }}
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="暂无未匹配候选"
                          />
                        ),
                      }}
                      pagination={{
                        current: backfillPage,
                        pageSize: PAGE_SIZE,
                        total: backfillQuery.data?.total ?? 0,
                        showSizeChanger: false,
                        onChange: setBackfillPage,
                      }}
                    />
                  </div>
                  <AdminMobileList
                    rows={backfillRows}
                    rowKey={(row) => row.id}
                    loading={backfillQuery.isFetching}
                    emptyContent={
                      backfillQuery.isLoading ? undefined : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="暂无未匹配候选"
                        />
                      )
                    }
                    page={backfillPage}
                    pageSize={PAGE_SIZE}
                    total={backfillQuery.data?.total ?? 0}
                    onPageChange={setBackfillPage}
                    renderItem={(row) => (
                      <article
                        className="admin-mobile-record"
                        role="button"
                        tabIndex={0}
                        aria-label={`手动指定 ${row.title} 的 TMDB ID`}
                        onClick={() => openManualAssign(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            openManualAssign(row)
                          }
                        }}
                      >
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title">
                            {row.title}
                            {row.title_original ? (
                              <div className="admin-mobile-record__meta">
                                <span>{row.title_original}</span>
                              </div>
                            ) : null}
                          </div>
                          {row.format ? <Tag>{row.format}</Tag> : null}
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span style={NUM_STYLE}>#{row.id}</span>
                          <span>{row.release_year ?? '-'} 年</span>
                          <span>Bangumi {row.bangumi_id ?? '-'}</span>
                          <span>系列 {row.series_id ?? '-'}</span>
                          <span>第 {row.season_number ?? '-'} 季</span>
                        </div>
                        <AdminRecordActions
                          actions={[
                            {
                              key: 'edit',
                              label: '编辑番剧',
                              onClick: () =>
                                void navigate({
                                  to: '/anime/$animeId/edit',
                                  params: { animeId: String(row.id) },
                                }),
                            },
                            {
                              key: 'manual',
                              label: '指定 TMDB ID',
                              icon: <EditOutlined />,
                              primary: true,
                              onClick: () => openManualAssign(row),
                            },
                            {
                              key: 'command',
                              label: '复制命令',
                              icon: <CopyOutlined />,
                              onClick: () => void copyCommand(command),
                            },
                          ]}
                          maxVisible={1}
                        />
                      </article>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'series',
            label: '系列继承',
            children: (
              <Card
                title="系列继承"
                extra={
                  <Space wrap>
                    <Space size={8}>
                      {/* 手机上标题栏空间有限：文案收进开关内部 */}
                      {isMobile ? null : (
                        <Typography.Text type="secondary">
                          包含 movie
                        </Typography.Text>
                      )}
                      <Switch
                        aria-label="包含 movie"
                        checkedChildren={isMobile ? '含电影' : undefined}
                        unCheckedChildren={isMobile ? '含电影' : undefined}
                        checked={includeMovies}
                        onChange={(checked) => {
                          setIncludeMovies(checked)
                          setSeriesPage(1)
                        }}
                      />
                    </Space>
                    <Tooltip title={isMobile ? '刷新' : undefined}>
                      <Button
                        icon={<ReloadOutlined />}
                        aria-label="刷新"
                        onClick={() => seriesQuery.refetch()}
                        loading={
                          seriesQuery.isFetching && !seriesQuery.isLoading
                        }
                      >
                        {isMobile ? null : '刷新'}
                      </Button>
                    </Tooltip>
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      disabled={seriesRows.length === 0}
                      loading={applyMutation.isPending}
                      onClick={confirmApplySeriesInherit}
                    >
                      应用继承
                    </Button>
                  </Space>
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    只展示同一系列内存在唯一已确认 TMDB 映射的番剧。应用后会创建
                    已确认的系列继承映射，资料和图片仍通过读取时回退到来源番剧。
                  </Typography.Paragraph>
                  {seriesError ? (
                    <Alert
                      type="error"
                      showIcon
                      message="系列继承候选加载失败"
                      description={errorDescription(seriesError)}
                    />
                  ) : null}
                  <div className="admin-table-wrap">
                    <Table<SeriesInheritCandidate>
                      rowKey={(row) =>
                        `${row.anime_id}:${row.source_mapping_id}`
                      }
                      columns={seriesColumns}
                      dataSource={seriesRows}
                      loading={seriesQuery.isLoading}
                      scroll={{ x: 920 }}
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="暂无可系列继承候选"
                          />
                        ),
                      }}
                      pagination={{
                        current: seriesPage,
                        pageSize: PAGE_SIZE,
                        total:
                          seriesRows.length === PAGE_SIZE
                            ? seriesPage * PAGE_SIZE + 1
                            : (seriesPage - 1) * PAGE_SIZE + seriesRows.length,
                        showSizeChanger: false,
                        onChange: setSeriesPage,
                      }}
                    />
                  </div>
                  <AdminMobileList
                    rows={seriesRows}
                    rowKey={(row) => `${row.anime_id}:${row.source_mapping_id}`}
                    loading={seriesQuery.isFetching}
                    emptyContent={
                      seriesQuery.isLoading ? undefined : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="暂无可系列继承候选"
                        />
                      )
                    }
                    page={seriesPage}
                    pageSize={PAGE_SIZE}
                    total={
                      seriesRows.length === PAGE_SIZE
                        ? seriesPage * PAGE_SIZE + 1
                        : (seriesPage - 1) * PAGE_SIZE + seriesRows.length
                    }
                    onPageChange={setSeriesPage}
                    renderItem={(row) => (
                      <article className="admin-mobile-record tmdb-mobile-record">
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title">
                            <Link
                              to="/anime/$animeId/edit"
                              params={{ animeId: String(row.anime_id) }}
                            >
                              {row.title}
                            </Link>
                          </div>
                          <Tag>{mediaTypeLabel(row.media_type)}</Tag>
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span style={NUM_STYLE}>#{row.anime_id}</span>
                          <span>第 {row.season_number ?? '-'} 季</span>
                          <span>{row.release_year ?? '-'}</span>
                          <span style={NUM_STYLE}>TMDB {row.tmdb_id}</span>
                        </div>
                        <div className="admin-mobile-record__body">
                          继承自{' '}
                          <Link
                            to="/anime/$animeId/edit"
                            params={{ animeId: String(row.source_anime_id) }}
                          >
                            {row.source_title ?? `#${row.source_anime_id}`}
                          </Link>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {' '}
                            · 第 {row.source_season_number ?? '-'} 季
                          </Typography.Text>
                        </div>
                        <div className="admin-mobile-record__badges">
                          {row.source_match_method ? (
                            <Tag color="blue">
                              {matchMethodLabel(row.source_match_method)}
                            </Tag>
                          ) : null}
                          <span style={NUM_STYLE}>
                            置信度 {percentText(row.source_confidence)}
                          </span>
                        </div>
                      </article>
                    )}
                  />
                </Space>
              </Card>
            ),
          },
        ]}
      />
      <Modal
        title="手动指定 TMDB ID"
        open={manualAssignRow !== null}
        okText="保存并同步资源"
        cancelText="取消"
        confirmLoading={
          manualAssignMutation.isPending ||
          syncMappingResourcesMutation.isPending
        }
        onCancel={closeManualAssign}
        onOk={() => void submitManualAssign()}
        destroyOnHidden
      >
        {manualAssignRow ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="首批仅处理没有已确认 TMDB ID 的番剧"
              description="保存后会先创建待确认的手动映射，再由后端同步 TMDB 简介、poster、logo 和 backdrop。"
            />
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{manualAssignRow.title}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                #{manualAssignRow.id} · {manualAssignRow.release_year ?? '-'} ·{' '}
                {manualAssignRow.format ?? '-'}
              </Typography.Text>
            </Space>
            <Form<ManualAssignFormValues>
              form={manualForm}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                label="TMDB 类型"
                name="media_type"
                rules={[{ required: true, message: '请选择 TMDB 类型' }]}
              >
                <Select options={MEDIA_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                label="TMDB ID"
                name="tmdb_id"
                rules={[{ required: true, message: '请输入 TMDB ID' }]}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                label="TMDB 季数"
                name="season_number"
                extra="TV 多季条目可填写；不确定可留空。"
              >
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Space>
        ) : null}
      </Modal>
    </Space>
  )
}
